import {
  Cancel,
  CheckCircle,
  Circle,
  Person,
  Send,
  Visibility,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { If } from 'src/components/If';
import { WalletInvitationLinkStatus } from 'src/services/invite-monitor';

export type OwnerWalletCardProps = {
  walletId: string | number;
  ownerId: string | number;
  name: string;
  ownerName?: string;
  guestName?: string;
  fundsCount: number;
  quotasCount: number;
  walletInvitationLinkStatus: WalletInvitationLinkStatus;
  walletButton?: {
    label: string;
    redirectTo: string;
  };
  isOwner?: boolean;
  onSendApproval?: () => void;
  onAcceptApproval?: () => void;
  onRejectApproval?: () => void;
};

export const OwnerGuestWalletCard = ({
  walletId,
  ownerId,
  name,
  ownerName,
  guestName,
  fundsCount,
  quotasCount,
  walletButton,
  walletInvitationLinkStatus,
  isOwner,
  onSendApproval,
  onAcceptApproval,
  onRejectApproval,
}: OwnerWalletCardProps) => {
  const lineStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 1,
  };

  const walletInvitationLinkStatusMessage = {
    draft: 'Rascunho',
    pending: 'Pendente',
    accepted: 'Aceito',
    rejected: 'Rejeitado',
  };

  const walletStatusIcon = (
    <Tooltip
      title={walletInvitationLinkStatusMessage[walletInvitationLinkStatus]}>
      <Circle
        color={
          walletInvitationLinkStatus === 'accepted'
            ? 'success'
            : walletInvitationLinkStatus === 'pending'
              ? 'warning'
              : 'info'
        }
        sx={{ fontSize: 12 }}
      />
    </Tooltip>
  );

  const cardBorderStyle = {
    draft: '1px dashed #a4a4a4',
    pending: '1px dashed #ffc72c',
    accepted: '1px solid #00e38c',
    rejected: '1px dashed #d32f2f',
  };

  return (
    <>
      <Card
        sx={{
          px: 1,
          border: cardBorderStyle[walletInvitationLinkStatus],
          position: 'relative',
          minHeight: 150,
        }}>
        <CardHeader
          title={
            <Box sx={lineStyles}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  variant="h6"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mr: 1,
                  }}>
                  {name}
                </Typography>
                <If condition={ownerName || guestName}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                    }}>
                    <Person sx={{ color: 'text.disabled' }} />
                    <Typography
                      sx={{
                        flex: 1,
                        textAlign: 'start',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mr: 1,
                        mt: 0.875,
                        fontWeight: 'bold',
                        fontSize: 12,
                        color: 'text.disabled',
                      }}>
                      {isOwner ? guestName : ownerName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.disabled">
                      {
                        walletInvitationLinkStatusMessage[
                          walletInvitationLinkStatus
                        ]
                      }
                    </Typography>
                  </Box>
                </If>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  flexShrink: 0,
                  ml: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}>
                {walletStatusIcon}
              </Typography>
            </Box>
          }
        />
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={lineStyles}>
            <Typography py={1} variant="body2">
              Subclasse de Cotas/Fundos
            </Typography>
            <Typography py={1} variant="body2" fontWeight="bold">
              {quotasCount}/{fundsCount}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: '#a4a4a4', mb: -3 }} />
        </CardContent>
        <CardActions sx={lineStyles}>
          <If condition={!isOwner}>
            <Button
              variant="text"
              component={Link}
              to={
                walletButton?.redirectTo ??
                `/monitor/${ownerId}/wallet/${walletId}`
              }
              fullWidth
              sx={{ color: '#646464', fontSize: 10, textTransform: 'unset' }}
              startIcon={<Visibility />}>
              {walletButton?.label ?? 'Clique para gerenciar'}
            </Button>
          </If>
          <If condition={!isOwner && walletInvitationLinkStatus === 'draft'}>
            <Button
              variant="text"
              color="primary"
              onClick={() => onSendApproval?.()}
              fullWidth
              sx={{
                color: 'primary.main',
                fontSize: 10,
                textTransform: 'unset',
              }}
              startIcon={<Send />}>
              Enviar para aprovação
            </Button>
          </If>
          <If condition={isOwner && walletInvitationLinkStatus === 'pending'}>
            <Button
              component={Link}
              to={`/monitor/${ownerId}/wallet/${walletId}`}
              startIcon={<Visibility />}
              sx={{
                color: 'text.disabled',
                fontSize: 10,
                textTransform: 'unset',
              }}>
              Clique para visualizar
            </Button>
            <Box sx={{ display: 'flex' }}>
              <IconButton title="Aceitar" onClick={() => onAcceptApproval?.()}>
                <CheckCircle sx={{ color: '#82CE57' }} />
              </IconButton>
              <IconButton title="Rejeitar" onClick={() => onRejectApproval?.()}>
                <Cancel sx={{ color: '#FF4747' }} />
              </IconButton>
            </Box>
          </If>
        </CardActions>
      </Card>
    </>
  );
};
